import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import moment from "Helpers/MomentCompat";
import { showNotification } from "Helpers/Toast";

import UseAuth from "Hooks/UseAuth";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";

import ScheduleCalendar from "./ScheduleCalendar";
import { toCalendarDay } from "./calendarModifiers";
import * as S from "./styles";

// Sessão CRONOGRAMA do projeto aportado (A12/A13 — D5/D21/D22) com APROVAÇÃO do
// admin (B04 — D11–D14 da parte 2, 03/09/2026).
//
// O que mudou em 03/09: o evento do PROPONENTE nasce **Pendente** e só aparece
// para o patrocinador depois que o admin aprova (o backend faz o recorte, não a
// tela). O proponente passou a poder EDITAR — mas apenas o que ainda está
// pendente: depois de aprovado, só o admin altera (D11). Para o patrocinador a
// sessão ganhou o **calendário ao lado** da lista (D13).
//
// Evento de 1 dia continua exibido de forma simplificada — só a data.
//
// PERÍODO DO PROJETO (03/09/2026): a aba passou a exibir também o início e o fim
// do projeto (`project_start_at`/`project_finish_at`). Ele **não é evento
// cadastrado** — vem do formulário base do projeto —, mas é a data principal, e
// sem ele a aba abria vazia ("Nenhum evento cadastrado") mesmo num projeto com
// período definido. Entra na lista como uma linha fixa e no calendário como
// faixa, e é somente leitura em qualquer perfil: quem a altera é o formulário
// do projeto.
const Schedule = ({ projectId, project = null }) => {
  const { api } = UseServices();
  const { parsedJWT } = UseAuth();

  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [saving, setSaving] = useState(false);

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  const getEvents = () => {
    api
      .post("/projects/dates", { project_id: projectId })
      .then((response) => setEvents(response.data || []))
      .catch((error) => HTTPErrorCallback(error));
  };

  useEffect(() => {
    if (projectId) getEvents();
    // eslint-disable-next-line
  }, [projectId]);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName("");
    setStartDate("");
    setFinishDate("");
  };

  const openNewForm = () => {
    closeForm();
    setShowForm(true);
  };

  // Só evento PENDENTE é editável pelo proponente (D11) — e o backend recusa
  // com 403 se a tela for burlada.
  const openEditForm = (event) => {
    setEditingId(event.id);
    setName(event.name || "");
    // Também normalizado: sem isto, abrir "Editar" pré-preencheria o campo com
    // o dia anterior e SALVAR deslocaria o evento de verdade — o bug de
    // exibição viraria bug de dado.
    setStartDate(moment(toCalendarDay(event.date)).format("YYYY-MM-DD"));
    setFinishDate(
      moment(toCalendarDay(event.finish_date || event.date)).format(
        "YYYY-MM-DD",
      ),
    );
    setShowForm(true);
  };

  const saveEvent = async () => {
    setSaving(true);

    try {
      if (editingId) {
        await api.post("/projects/dates/update", {
          project_id: projectId,
          date_id: editingId,
          name,
          date: startDate,
          finish_date: finishDate,
        });
      } else {
        await api.post("/projects/dates/create", {
          project_id: projectId,
          name,
          date: startDate,
          finish_date: finishDate,
        });
      }

      showNotification(
        "success",
        "Sucesso!",
        editingId
          ? "Evento atualizado com sucesso!"
          : "Evento criado com sucesso!",
      );

      closeForm();
      getEvents();
    } catch (error) {
      HTTPErrorCallback(error);
    } finally {
      setSaving(false);
    }
  };

  /*
   * Toda data exibida aqui passa por `toCalendarDay` (03/09/2026): ela lê o dia
   * em UTC — que é como o backend grava — e o fixa ao meio-dia local, para que
   * nenhum fuso empurre a exibição um dia para trás. Sem isso, o período
   * gravado como `2026-08-20 00:00 UTC` aparecia como 19/08 em São Paulo.
   *
   * A mesma função alimenta o calendário: é o que garante que a lista e a faixa
   * não possam mostrar dias diferentes.
   */
  const formatDay = (value) =>
    moment(toCalendarDay(value)).format("DD/MM/YYYY");

  const isSingleDay = (event) =>
    !event.finish_date ||
    formatDay(event.finish_date) === formatDay(event.date);

  // NULL = linha anterior à coluna de aprovação (backfill do 20260903_02), lida
  // como Aprovado.
  const statusOf = (event) => event.approval_status || "Aprovado";

  const isPending = (event) => statusOf(event) === "Pendente";

  const periodStart = project?.project_start_at;
  const periodFinish = project?.project_finish_at;
  const hasPeriod = !!periodStart && !!periodFinish;

  const eventList = (
    <S.Events>
      {/* Linha fixa, antes dos eventos: é a data que dá contexto a todas as
          outras. Sem selo de status e sem "Editar" — não é evento. */}
      <S.Period>
        <h2>Período do projeto</h2>
        {hasPeriod ? (
          <span>
            <strong>De:</strong> {formatDay(periodStart)} <strong>até</strong>{" "}
            {formatDay(periodFinish)}
          </span>
        ) : (
          <span>
            <strong>—</strong> (as datas de início e fim ainda não foram
            informadas no cadastro do projeto)
          </span>
        )}
      </S.Period>

      {!events.length ? <span>Nenhum evento cadastrado.</span> : null}

      {events.map((event) => (
        <S.Event key={event.id}>
          <h2>{event.name}</h2>

          {isSingleDay(event) ? (
            <span>
              <strong>Data:</strong> {formatDay(event.date)}
            </span>
          ) : (
            <span>
              <strong>Período:</strong> {formatDay(event.date)} –{" "}
              {formatDay(event.finish_date)}
            </span>
          )}

          {/* O patrocinador só recebe evento aprovado, então o status seria
              ruído na tela dele. Para o proponente ele é a informação
              principal: é o que diz se o patrocinador já vê o evento. */}
          {!is_sponsor ? (
            <>
              <S.Status status={statusOf(event)}>{statusOf(event)}</S.Status>

              {isPending(event) ? (
                <Button
                  type="button"
                  text="Editar"
                  outline
                  onClick={() => openEditForm(event)}
                />
              ) : (
                <S.Locked>
                  Evento já avaliado pela Equipe Sevenx — para alterar, entre em
                  contato.
                </S.Locked>
              )}
            </>
          ) : null}
        </S.Event>
      ))}
    </S.Events>
  );

  return (
    <S.Wrapper>
      <S.Section>
        <span>Cronograma</span>
        <hr />
      </S.Section>

      {!is_sponsor ? (
        <S.Buttons>
          <Button
            type="button"
            text={showForm ? "Fechar" : "Criar Evento"}
            outline={showForm}
            onClick={() => (showForm ? closeForm() : openNewForm())}
          />
        </S.Buttons>
      ) : null}

      {showForm && !is_sponsor ? (
        <S.FormBlock>
          <Form autoComplete="off">
            <Input
              name="event_name"
              label="Nome do evento"
              placeholder="Nome do evento"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              name="event_start"
              label="Data de início"
              type="date"
              placeholder="Data de início"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
            <Input
              name="event_finish"
              label="Data de término"
              type="date"
              placeholder="Data de término"
              value={finishDate}
              onChange={(e) => setFinishDate(e.target.value)}
              required
            />

            {/* Helper pedido no PDF (item e.iii.3.b): o evento de um único dia
                se cadastra repetindo a data, e sem esta linha o proponente fica
                sem saber o que pôr no término. */}
            <S.Helper>
              Evento de um único dia: use a mesma data no início e no término.
            </S.Helper>

            <S.Helper>
              O evento passa pela avaliação da Equipe Sevenx antes de aparecer
              para o patrocinador.
            </S.Helper>

            <Button
              type="button"
              text={editingId ? "Salvar Alterações" : "Salvar Evento"}
              disabled={saving || !name || !startDate || !finishDate}
              onClick={saveEvent}
            />
          </Form>
        </S.FormBlock>
      ) : null}

      {/* Patrocinador: lista à esquerda, calendário à direita (D13). */}
      {is_sponsor ? (
        <S.SponsorLayout>
          {eventList}
          <S.CalendarColumn>
            <ScheduleCalendar
              events={events}
              projectPeriod={hasPeriod ? { periodStart, periodFinish } : null}
            />
          </S.CalendarColumn>
        </S.SponsorLayout>
      ) : (
        eventList
      )}
    </S.Wrapper>
  );
};

export default Schedule;
